// How long each page takes to load on this device, measured where staff feel it: from the moment the
// route changes to the moment the last of the page's own calls has answered.
//
// - A page's own load is the chain of calls that starts with the page: a call belongs to it while it starts
//   before the page has been quiet for GAP_MS (so a request that only starts once an earlier one has answered
//   still counts). Calls that start after a quiet gap (a tap, a refresh) are counted but are not part of "ready".
// - Background polling is left out, and only the path of a call is kept (never its query string).
// - The last MAX_PAGES pages are kept on the device (localStorage) so a report can be read after the fact:
//   Settings > Performance.

const STORE_KEY = 'oa_page_timings';
const MAX_PAGES = 40;
const GAP_MS = 350;
const WATCH_MS = 8000; // calls later than this after the route change are not attributed to the page
const QUIET = /call-notification|\/_oa\//;

let current = null;
let finalizeTimer = null;

const round = (n) => Math.round(n);
export const stripQuery = (url) => String(url || '').split('?')[0].replace(/^https?:\/\/[^/]+/, '');

const readStored = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const writeStored = (pages) => {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(pages.slice(-MAX_PAGES)));
  } catch {
    // storage full or blocked: the report simply keeps what is in memory
  }
};

// The calls that make up the page's own load: from the first call, each next call that begins before the
// previous ones have been finished for GAP_MS.
const loadChain = (calls) => {
  const ordered = [...calls].sort((a, b) => a.startMs - b.startMs);
  const chain = [];
  let busyUntil = -Infinity;
  for (const c of ordered) {
    if (chain.length > 0 && c.startMs > busyUntil + GAP_MS) break;
    chain.push(c);
    busyUntil = Math.max(busyUntil, c.startMs + c.ms);
  }
  return chain;
};

export const summarise = (page) => {
  const burst = loadChain(page.calls);
  const slowest = burst.reduce((best, c) => (!best || c.ms > best.ms ? c : best), null);
  const sources = burst.reduce((acc, c) => ({ ...acc, [c.source]: (acc[c.source] || 0) + 1 }), {});
  return {
    path: page.path,
    at: page.at,
    readyMs: burst.length ? Math.max(...burst.map((c) => c.startMs + c.ms)) : 0,
    calls: burst.length,
    laterCalls: page.calls.length - burst.length,
    slowest: slowest ? { path: slowest.path, ms: slowest.ms } : null,
    sources,
    detail: page.calls,
  };
};

const finalizeCurrent = () => {
  if (finalizeTimer) {
    clearTimeout(finalizeTimer);
    finalizeTimer = null;
  }
  if (!current) return;
  const page = current;
  current = null;
  if (page.calls.length === 0) return; // a page that asked for nothing has nothing to report
  writeStored([...readStored(), summarise(page)]);
};

/** Call when the route changes. */
export function routeChanged(path) {
  finalizeCurrent();
  current = { path, at: Date.now(), t0: performance.now(), calls: [] };
  finalizeTimer = setTimeout(finalizeCurrent, WATCH_MS + 500);
}

/** Call when an API call has finished. `start` is performance.now() at the moment it was sent. */
export function recordCall({ path, method = 'GET', start, ms, status = 0, source = 'cloud' }) {
  if (!current) return;
  const clean = stripQuery(path);
  if (QUIET.test(clean)) return;
  const startMs = start - current.t0;
  if (startMs < 0 || startMs > WATCH_MS) return; // began before this page (or long after it loaded)
  current.calls.push({ path: clean, method: String(method).toUpperCase(), startMs: round(startMs), ms: round(ms), status, source });
}

/** The pages kept on the device, oldest first, plus the one being watched now. */
export function getReport() {
  const stored = readStored();
  const live = current && current.calls.length ? [summarise(current)] : [];
  return [...stored, ...live];
}

export function clearReport() {
  finalizeCurrent();
  current = null;
  try {
    window.localStorage.removeItem(STORE_KEY);
  } catch {
    // nothing stored, or storage unavailable
  }
}

export const timingConstants = { GAP_MS, WATCH_MS, MAX_PAGES };
